/**
 * usePwaInstallReward — Awards a one-time +100 pt bonus when the user adds
 * GoalDigger to their home screen.
 *
 * Detection:
 *   - Android / Chrome / Edge / desktop  → `appinstalled` event
 *   - iOS Safari                         → first launch where the app is
 *                                          running in standalone display mode
 *
 * Idempotency:
 *   - Server-enforced via `add_user_points` (unique on
 *     user_id + action + reference_id). We pass the user's id as the
 *     reference_id so a single user can only be awarded once even if they
 *     clear localStorage and reinstall.
 */
import { useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { usePoints } from "@/hooks/usePoints";
import { useIsStandalone } from "@/hooks/useIsStandalone";
import {
  markInstallRewarded,
  readInstallPromptState,
} from "@/lib/installPromptState";
import { POINTS } from "@/utils/gamificationUtils";
import { logger } from "@/utils/logger";

export function usePwaInstallReward(): void {
  const { user } = useAuth();
  const { awardPoints } = usePoints();
  const isStandalone = useIsStandalone();
  const inFlight = useRef(false);

  useEffect(() => {
    if (!user?.id) return;

    const tryAward = async (source: "appinstalled" | "standalone") => {
      if (inFlight.current) return;
      const state = readInstallPromptState();
      if (state.rewardedAt) return;
      inFlight.current = true;
      try {
        const ok = await awardPoints(
          POINTS.INSTALL_PWA,
          "installed_pwa",
          user.id, // reference_id = user.id → one award per user, ever
          "Added GoalDigger to home screen"
        );
        if (ok) {
          markInstallRewarded();
          logger.info("[PWA] +100 install reward granted", { source });
        } else {
          // Duplicate or already awarded — mark locally so we stop checking.
          markInstallRewarded();
        }
      } catch (e) {
        logger.warn("[PWA] install reward award failed:", e);
      } finally {
        inFlight.current = false;
      }
    };

    // iOS / Android standalone-launch detection
    if (isStandalone) {
      void tryAward("standalone");
    }

    // Android / Chrome / Edge / desktop install event
    const onInstalled = () => { void tryAward("appinstalled"); };
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, [user?.id, isStandalone, awardPoints]);
}
